/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getAzureService } from 'src/core/services/azure/azureService';
import { RoleAssignmentPropertiesSwagger } from 'src/core/services/azure/azureSwaggers';
import { LuisConstants } from 'src/core/utils/LuisConstants';

type ContainerPermissionsDto = {
    hasBlobAccess: boolean;
    isLoading: boolean;
    error?: string;
};

const messages = defineMessages({
    haveNotPermission: {
        id: 'customText.connectionPage.haveNotPermission',
        defaultMessage:
            'The selected workspace does not have a Storage Blob Data Reader permission on the Azure storage account "{storageAccountName}". Please go to Azure portal to set the required permission to access. '
    }
});

export const useStorageAccountRoles = (amlWorkspaceId: string) => {
    const { rootStore } = useGlobalState();
    const { armToken } = rootStore.azureStore.session.userTokens;
    const { csAccountStore } = rootStore.azureStore.session;
    const { localize } = useLocalization();

    const [containerPermissions, updateContainerPermissions] = useUpdateState<ContainerPermissionsDto>({
        hasBlobAccess: false,
        isLoading: false
    });

    React.useEffect(() => {
        if (!amlWorkspaceId) {
            return;
        }
        (async () => {
            try {
                updateContainerPermissions({ isLoading: true });

                const storageRoles = await getAzureService().listAzureResourceRoleAssignments({
                    armToken,
                    scope: csAccountStore?.ownedStorageId,
                    principalId: amlWorkspaceId
                });

                const accountRoles = getRolesByPrincipalId(storageRoles.value, amlWorkspaceId);
                if (areAuthoringResourceValidRoles(accountRoles)) {
                    updateContainerPermissions({ hasBlobAccess: true, isLoading: false, error: null });

                    return;
                }

                updateContainerPermissions({
                    hasBlobAccess: false,
                    isLoading: false,
                    error: localize(messages.haveNotPermission, { storageAccountName: csAccountStore.ownedStorageAccountInfo.name })
                });
            } catch (error) {
                updateContainerPermissions({
                    hasBlobAccess: false,
                    isLoading: false,
                    error: error.message
                });
            }
        })();
    }, [amlWorkspaceId]);

    return { ...containerPermissions };
};

const getRolesByPrincipalId = (roles: RoleAssignmentPropertiesSwagger[], id: string): string[] =>
    roles.filter(role => role.properties.principalId === id).map(role => role.properties.roleDefinitionId.split('/').pop());

const isOwnerOrContributorOrReader = (rolesIds: string[]) =>
    rolesIds.some(
        id =>
            id === LuisConstants.Azure.AzureRoleDefinitionId.StorageBlobDataOwner ||
            id === LuisConstants.Azure.AzureRoleDefinitionId.StorageBlobDataContributor ||
            id === LuisConstants.Azure.AzureRoleDefinitionId.StorageBlobDataReader
    );
const areAuthoringResourceValidRoles = (rolesIds: string[]) => isOwnerOrContributorOrReader(rolesIds);
